import { moduleTypes } from '../../util/constants';
import { getAxiosInstance } from '../../util/service';

/**
 * Assign Invoice Actions (v2)
 *
 * Changes from original:
 *  - Removed isManagement() call inside action → passed as parameter
 *  - Cleaner error returns with consistent shape
 *  - Filter utilities remain pure functions (no Redux dependency)
 *  - Removed broken `fitlerInvoiceList` duplicate (typo, had stray return referencing undefined `response`)
 */

const TRUCK_FILTER_OPTIONS = {
    Available:  { key: 'Available', value: 'available' },
    Assigned:   { key: 'Assigned', value: 'Assigned,Accepted,Enroute,Arrived' },
    Loaded:     { key: 'Loaded',   value: 'Arrived,Hooked' },
    Doubled:    { key: 'Doubled',  value: 'Arrived,Hooked' },
};

// ── Thunk: Assign invoice to driver + truck ─────────────────────────────
export const assignInvoice = (invoiceId, driverId, truckId, isMgmt) => {
    return async () => {
        const url = `dashboard/${invoiceId}/assign?driverId=${encodeURIComponent(driverId)}&truckId=${encodeURIComponent(truckId)}&isManagement=${isMgmt}`;
        try {
            const response = await getAxiosInstance(moduleTypes.DISPATCH).put(url);
            if (response.status === 200) {
                return { statusCode: response.status, statusMsg: response.data.statusMsg };
            }
            return { statusCode: null, statusMsg: response.data.statusMsg };
        } catch (ex) {
            const serverMsg = ex.response && ex.response.data && ex.response.data.statusMsg;
            return { statusCode: ex.response ? ex.response.status : null, statusMsg: serverMsg || ex.message };
        }
    };
};

// ── Thunk: Assign invoice to company (management mode) ──────────────────
export const assignInvoiceToCompany = (invoiceId, companyId, isMgmt) => {
    return async () => {
        const url = `dashboard/${invoiceId}/assignToCompany/${companyId}?isManagement=${isMgmt}`;
        try {
            const response = await getAxiosInstance(moduleTypes.DISPATCH).put(url);
            if (response.status === 200) {
                return { statusCode: response.status, statusMsg: response.data.statusMsg };
            }
            return { statusCode: null, statusMsg: response.data.statusMsg };
        } catch (ex) {
            const serverMsg = ex.response && ex.response.data && ex.response.data.statusMsg;
            return { statusCode: ex.response ? ex.response.status : null, statusMsg: serverMsg || ex.message };
        }
    };
};

// ── Pure: Filter invoice list by pickup status + priority ────────────────
export const filterInvoiceList = (list, invoiceFilters) => {
    const pickupStatus = Object.keys(invoiceFilters.pickup).filter(
        (k) => invoiceFilters.pickup[k].selected
    );
    const priorityStatus = Object.keys(invoiceFilters.priority).filter(
        (k) => invoiceFilters.priority[k].selected
    );

    if (pickupStatus.length === 0 && priorityStatus.length === 0) return list;

    let filtered = list;
    if (pickupStatus.length > 0) {
        filtered = filtered.filter((item) => pickupStatus.includes(item.callStatus));
    }
    if (priorityStatus.length > 0) {
        filtered = filtered.filter((item) => priorityStatus.includes(String(item.priority)));
    }
    return filtered;
};

// ── Pure: Filter truck list by status toggles ────────────────────────────
export const filterTruckList = (list, selectedFilters) => {
    const selectedKeys = Object.keys(selectedFilters).filter((k) => selectedFilters[k].selected);
    if (selectedKeys.length === 0) return list;

    const statusList = [];

    if (selectedKeys.includes(TRUCK_FILTER_OPTIONS.Available.key)) {
        statusList.push(TRUCK_FILTER_OPTIONS.Available.value);
    }
    if (selectedKeys.includes(TRUCK_FILTER_OPTIONS.Assigned.key)) {
        const assignList = TRUCK_FILTER_OPTIONS.Assigned.value.split(',');
        if (selectedKeys.includes(TRUCK_FILTER_OPTIONS.Loaded.key) || selectedKeys.includes(TRUCK_FILTER_OPTIONS.Doubled.key)) {
            const idx = assignList.indexOf('Arrived');
            if (idx !== -1) assignList.splice(idx, 1);
        }
        statusList.push(...assignList);
    }

    return list.filter((item) => {
        const truckStatuses = item.truckStatus.split(',');
        if (isStatusMatch(statusList, truckStatuses)) return true;
        if (isStatusMatch(TRUCK_FILTER_OPTIONS.Loaded.value.split(','), truckStatuses) && item.invoiceCount === 1) return true;
        if (isStatusMatch(TRUCK_FILTER_OPTIONS.Doubled.value.split(','), truckStatuses) && item.invoiceCount > 1) return true;
        return false;
    });
};

const isStatusMatch = (statusList, truckStatusList) =>
    truckStatusList.some((status) => statusList.includes(status));